/**
 * ChannelManager Component
 *
 * Admin component for managing channels with:
 * - Channel list display with statistics
 * - Create new channels via ChannelForm
 * - Edit and delete channels
 * - Archive/unarchive functionality
 * - Member management
 *
 * @module components/admin/ChannelManager
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Hash,
  Plus,
  RefreshCw,
  Loader2,
  AlertCircle,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  Users,
  MessageSquare,
  Globe,
  FolderKanban,
  Shield,
  Archive,
  ArchiveRestore,
  Clock,
  Search,
  Filter,
  X,
  MoreVertical
} from 'lucide-react';
import ChannelForm from './ChannelForm.jsx';

// ============================================
// API Functions
// ============================================

/**
 * Fetch all channels from the API
 * @param {object} options - Query options
 * @returns {Promise<{data: Array, pagination: object}>} Channels and pagination
 */
async function fetchChannels(options = {}) {
  const params = new URLSearchParams();
  if (options.type) params.set('type', options.type);
  if (options.projectId) params.set('projectId', options.projectId);
  if (options.includeArchived) params.set('includeArchived', 'true');
  if (options.page) params.set('page', options.page);
  if (options.limit) params.set('limit', options.limit);

  const response = await fetch(`/api/channels?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch channels: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create a new channel
 * @param {object} channelData - Channel data
 * @returns {Promise<object>} Created channel
 */
async function createChannel(channelData) {
  const response = await fetch('/api/channels', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(channelData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to create channel: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update an existing channel
 * @param {string} id - Channel ID
 * @param {object} channelData - Channel data
 * @returns {Promise<object>} Updated channel
 */
async function updateChannel(id, channelData) {
  const response = await fetch(`/api/channels/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(channelData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to update channel: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete a channel
 * @param {string} id - Channel ID
 * @returns {Promise<void>}
 */
async function deleteChannel(id) {
  const response = await fetch(`/api/channels/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to delete channel: ${response.statusText}`);
  }
}

/**
 * Fetch channel members
 * @param {string} channelId - Channel ID
 * @returns {Promise<object>} Members data
 */
async function fetchChannelMembers(channelId) {
  const response = await fetch(`/api/channels/${channelId}/members`);
  if (!response.ok) {
    throw new Error(`Failed to fetch members: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Add a member to a channel
 * @param {string} channelId - Channel ID
 * @param {object} memberData - { userId } or { agentId }
 * @returns {Promise<object>} Created member
 */
async function addChannelMember(channelId, memberData) {
  const response = await fetch(`/api/channels/${channelId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(memberData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to add member: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Remove a member from a channel
 * @param {string} channelId - Channel ID
 * @param {string} memberId - Member ID
 * @returns {Promise<void>}
 */
async function removeChannelMember(channelId, memberId) {
  const response = await fetch(`/api/channels/${channelId}/members/${memberId}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to remove member: ${response.statusText}`);
  }
}

/**
 * Fetch all agents
 * @returns {Promise<Array>} Agents list
 */
async function fetchAgents() {
  const response = await fetch('/api/agents');
  if (!response.ok) {
    return [];
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch all users
 * @returns {Promise<Array>} Users list
 */
async function fetchUsers() {
  const response = await fetch('/api/users');
  if (!response.ok) {
    return [];
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch available projects for channel binding
 * @returns {Promise<Array>} Projects list
 */
async function fetchProjects() {
  const response = await fetch('/api/projects');
  if (!response.ok) {
    return [];
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch available roles for role-restricted channels
 * @returns {Promise<Array>} Roles list
 */
async function fetchRoles() {
  const response = await fetch('/api/roles');
  if (!response.ok) {
    return [];
  }
  const data = await response.json();
  return data.data || [];
}

// ============================================
// Sub-Components
// ============================================

/**
 * Channel type badge
 */
function TypeBadge({ type }) {
  const config = {
    public: { icon: Globe, label: 'Public', className: 'bg-green-500/20 text-green-400' },
    project: { icon: FolderKanban, label: 'Project', className: 'bg-blue-500/20 text-blue-400' },
    role_restricted: { icon: Shield, label: 'Restricted', className: 'bg-amber-500/20 text-amber-400' }
  };

  const { icon: Icon, label, className } = config[type] || config.public;

  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

/**
 * Channel statistics display
 */
function ChannelStats({ memberCount, messageCount }) {
  return (
    <div className="flex items-center gap-4 text-xs text-gray-500">
      <span className="flex items-center gap-1">
        <Users className="w-3.5 h-3.5" />
        {memberCount ?? 0} members
      </span>
      <span className="flex items-center gap-1">
        <MessageSquare className="w-3.5 h-3.5" />
        {messageCount ?? 0} messages
      </span>
    </div>
  );
}

/**
 * Channel list item with expand/collapse
 */
function ChannelItem({
  channel,
  onEdit,
  onDelete,
  onToggleArchive,
  onViewMembers,
  isDeleting,
  isArchiving
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`bg-gray-800 border rounded-lg overflow-hidden transition-colors ${
      channel.isArchived ? 'border-gray-700/50 opacity-60' : 'border-gray-700'
    }`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg ${channel.isArchived ? 'bg-gray-700' : 'bg-cyan-500/10'}`}>
              <Hash className={`w-5 h-5 ${channel.isArchived ? 'text-gray-500' : 'text-cyan-400'}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-white truncate">
                  {channel.displayName || channel.name}
                </h3>
                {channel.isArchived && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-gray-600/50 text-gray-400">
                    Archived
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 truncate">#{channel.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <TypeBadge type={channel.type} />

            {/* Actions */}
            <button
              onClick={() => onViewMembers(channel)}
              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
              title="View Members"
            >
              <Users className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(channel)}
              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onToggleArchive(channel)}
              disabled={isArchiving}
              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-amber-400 disabled:opacity-50"
              title={channel.isArchived ? 'Unarchive' : 'Archive'}
            >
              {isArchiving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : channel.isArchived ? (
                <ArchiveRestore className="w-4 h-4" />
              ) : (
                <Archive className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => onDelete(channel.id)}
              disabled={isDeleting}
              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-red-400 disabled:opacity-50"
              title="Delete"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 rounded hover:bg-gray-700 text-gray-400"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {channel.description && (
          <p className="mt-2 text-sm text-gray-400 line-clamp-2">{channel.description}</p>
        )}

        <div className="mt-3">
          <ChannelStats
            memberCount={channel.memberCount}
            messageCount={channel.messageCount}
          />
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-700 text-sm text-gray-400 space-y-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-gray-500">ID:</span>
              <span className="ml-2 font-mono text-xs">{channel.id}</span>
            </div>
            <div>
              <span className="text-gray-500">Type:</span>
              <span className="ml-2">{channel.type}</span>
            </div>
            {channel.project && (
              <div>
                <span className="text-gray-500">Project:</span>
                <span className="ml-2">{channel.project.displayName || channel.project.name}</span>
              </div>
            )}
            {channel.requiredRoles?.length > 0 && (
              <div className="col-span-2">
                <span className="text-gray-500">Required Roles:</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {channel.requiredRoles.map(role => (
                    <span key={role} className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <span className="text-gray-500">Created:</span>
              <span className="ml-2">{new Date(channel.createdAt).toLocaleDateString()}</span>
            </div>
            {channel.updatedAt && (
              <div>
                <span className="text-gray-500">Updated:</span>
                <span className="ml-2">{new Date(channel.updatedAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Filter dropdown
 */
function FilterDropdown({ value, onChange, options, label, icon: Icon }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="
          appearance-none pl-8 pr-8 py-2 rounded-md
          bg-gray-800 border border-gray-700 text-gray-300
          hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500
          text-sm cursor-pointer
        "
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
    </div>
  );
}

/**
 * Members modal with add/remove functionality
 */
function MembersModal({ channel, members, isLoading, onClose, onMemberAdded, onMemberRemoved }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [addType, setAddType] = useState('agent'); // 'agent' or 'user'
  const [selectedId, setSelectedId] = useState('');
  const [availableAgents, setAvailableAgents] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState(null);

  // Load agents and users when add form is shown
  useEffect(() => {
    if (showAddForm) {
      setLoadingOptions(true);
      Promise.all([fetchAgents(), fetchUsers()])
        .then(([agents, users]) => {
          // Filter out already added members
          const memberAgentIds = members.filter(m => m.agent).map(m => m.agent.id);
          const memberUserIds = members.filter(m => m.user).map(m => m.user.id);
          setAvailableAgents(agents.filter(a => !memberAgentIds.includes(a.id)));
          setAvailableUsers(users.filter(u => !memberUserIds.includes(u.id)));
        })
        .finally(() => setLoadingOptions(false));
    }
  }, [showAddForm, members]);

  const handleAdd = async () => {
    if (!selectedId) return;
    setIsAdding(true);
    setError(null);
    try {
      const memberData = addType === 'agent' ? { agentId: selectedId } : { userId: selectedId };
      await addChannelMember(channel.id, memberData);
      setShowAddForm(false);
      setSelectedId('');
      onMemberAdded?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (memberId) => {
    setRemovingId(memberId);
    setError(null);
    try {
      await removeChannelMember(channel.id, memberId);
      onMemberRemoved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingId(null);
    }
  };

  const options = addType === 'agent' ? availableAgents : availableUsers;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            <h3 className="font-medium text-white">
              Members of #{channel.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-700 text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Add Member Form */}
        {showAddForm ? (
          <div className="p-4 border-b border-gray-700 space-y-3">
            <div className="flex gap-2">
              <button
                onClick={() => { setAddType('agent'); setSelectedId(''); }}
                className={`flex-1 py-2 rounded-md text-sm transition-colors ${
                  addType === 'agent'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                Agent
              </button>
              <button
                onClick={() => { setAddType('user'); setSelectedId(''); }}
                className={`flex-1 py-2 rounded-md text-sm transition-colors ${
                  addType === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                User
              </button>
            </div>

            {loadingOptions ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select {addType}...</option>
                {options.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {opt.displayName || opt.name || opt.username}
                  </option>
                ))}
              </select>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => { setShowAddForm(false); setSelectedId(''); setError(null); }}
                className="flex-1 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={!selectedId || isAdding}
                className="flex-1 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isAdding ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 border-b border-gray-700">
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Member
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No members yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map(member => (
                <div
                  key={member.id}
                  className="flex items-center gap-3 p-2 rounded-lg bg-gray-700/50 group"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                    {member.user?.avatarUrl || member.agent?.avatarUrl ? (
                      <img
                        src={member.user?.avatarUrl || member.agent?.avatarUrl}
                        alt=""
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <span className="text-sm text-gray-400">
                        {(member.user?.displayName || member.agent?.displayName || '?')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {member.user?.displayName || member.agent?.displayName || member.user?.username || member.agent?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {member.type === 'agent' ? 'Agent' : 'User'}
                      {member.joinedAt && ` • Joined ${new Date(member.joinedAt).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(member.id)}
                    disabled={removingId === member.id}
                    className="p-1.5 rounded hover:bg-gray-600 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    title="Remove member"
                  >
                    {removingId === member.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700">
          <p className="text-xs text-gray-500 text-center">
            {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Delete confirmation modal
 */
function DeleteConfirmModal({ channel, onConfirm, onCancel, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full bg-red-500/10">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-white">Delete Channel</h3>
        </div>

        <p className="text-gray-400 mb-2">
          Are you sure you want to delete <strong className="text-white">#{channel.name}</strong>?
        </p>
        <p className="text-sm text-gray-500 mb-6">
          This will permanently delete the channel and all its messages. This action cannot be undone.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete Channel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * ChannelManager Component
 *
 * Manages channels with list display, create/edit form, and actions.
 */
export default function ChannelManager() {
  // State
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [channelToDelete, setChannelToDelete] = useState(null);

  // Members modal state
  const [membersModal, setMembersModal] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  // Resources for form
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Filter options
  const typeFilterOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'public', label: 'Public' },
    { value: 'project', label: 'Project' },
    { value: 'role_restricted', label: 'Restricted' }
  ];

  // Load channels
  const loadChannels = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchChannels({
        type: typeFilter !== 'all' ? typeFilter : undefined,
        includeArchived: showArchived,
        limit: 100
      });
      setChannels(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [typeFilter, showArchived]);

  // Load projects and roles for form
  const loadFormResources = useCallback(async () => {
    setProjectsLoading(true);
    setRolesLoading(true);
    try {
      const [projectsData, rolesData] = await Promise.all([
        fetchProjects(),
        fetchRoles()
      ]);
      setProjects(projectsData);
      setRoles(rolesData);
    } finally {
      setProjectsLoading(false);
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    if (showForm) {
      loadFormResources();
    }
  }, [showForm, loadFormResources]);

  // Filtered channels
  const filteredChannels = useMemo(() => {
    if (!searchQuery) return channels;
    const query = searchQuery.toLowerCase();
    return channels.filter(ch =>
      ch.name.toLowerCase().includes(query) ||
      ch.displayName?.toLowerCase().includes(query) ||
      ch.description?.toLowerCase().includes(query)
    );
  }, [channels, searchQuery]);

  // Handlers
  const handleSubmit = useCallback(async (formData) => {
    if (editingChannel) {
      await updateChannel(editingChannel.id, formData);
    } else {
      await createChannel(formData);
    }
    await loadChannels();
    setShowForm(false);
    setEditingChannel(null);
  }, [editingChannel, loadChannels]);

  const handleEdit = useCallback((channel) => {
    setEditingChannel(channel);
    setShowForm(true);
  }, []);

  const handleDeleteClick = useCallback((channel) => {
    setChannelToDelete(channel);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!channelToDelete) return;
    setDeletingId(channelToDelete.id);
    try {
      await deleteChannel(channelToDelete.id);
      await loadChannels();
      setChannelToDelete(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  }, [channelToDelete, loadChannels]);

  const handleToggleArchive = useCallback(async (channel) => {
    setArchivingId(channel.id);
    try {
      await updateChannel(channel.id, { isArchived: !channel.isArchived });
      await loadChannels();
    } catch (err) {
      alert(err.message);
    } finally {
      setArchivingId(null);
    }
  }, [loadChannels]);

  const handleViewMembers = useCallback(async (channel) => {
    setMembersModal(channel);
    setMembersLoading(true);
    try {
      const result = await fetchChannelMembers(channel.id);
      setMembers(result.data || []);
    } catch (err) {
      console.error('Failed to load members:', err);
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingChannel(null);
  }, []);

  // Render
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/10">
            <Hash className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Channel Management</h1>
            <p className="text-sm text-gray-400">Create and manage messaging channels</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadChannels}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => { setEditingChannel(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
          >
            <Plus className="w-4 h-4" />
            Create Channel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search channels..."
            className="w-full pl-10 pr-4 py-2 rounded-md bg-gray-800 border border-gray-700 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Type filter */}
        <FilterDropdown
          value={typeFilter}
          onChange={setTypeFilter}
          options={typeFilterOptions}
          icon={Filter}
        />

        {/* Show archived toggle */}
        <label className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 cursor-pointer hover:border-gray-600">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-gray-800"
          />
          <Archive className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-400">Show archived</span>
        </label>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 rounded-md bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Form (slide-in panel) */}
      {showForm && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <ChannelForm
            projects={projects}
            projectsLoading={projectsLoading}
            roles={roles}
            rolesLoading={rolesLoading}
            initialValues={editingChannel}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditing={!!editingChannel}
            submitLabel={editingChannel ? 'Save Changes' : 'Create Channel'}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && !channels.length && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !channels.length && !showForm && (
        <div className="text-center py-12">
          <Hash className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300">No channels found</h3>
          <p className="text-gray-500 mt-1">
            {typeFilter !== 'all' || searchQuery
              ? 'Try adjusting your filters'
              : 'Create a channel to get started'}
          </p>
          {!searchQuery && typeFilter === 'all' && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
            >
              Create Your First Channel
            </button>
          )}
        </div>
      )}

      {/* Channel list */}
      {filteredChannels.length > 0 && (
        <div className="space-y-3">
          {/* Stats bar */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              {filteredChannels.length} channel{filteredChannels.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </span>
          </div>

          {/* List */}
          {filteredChannels.map((channel) => (
            <ChannelItem
              key={channel.id}
              channel={channel}
              onEdit={handleEdit}
              onDelete={() => handleDeleteClick(channel)}
              onToggleArchive={handleToggleArchive}
              onViewMembers={handleViewMembers}
              isDeleting={deletingId === channel.id}
              isArchiving={archivingId === channel.id}
            />
          ))}
        </div>
      )}

      {/* Members Modal */}
      {membersModal && (
        <MembersModal
          channel={membersModal}
          members={members}
          isLoading={membersLoading}
          onClose={() => {
            setMembersModal(null);
            setMembers([]);
          }}
          onMemberAdded={async () => {
            // Refresh members list
            const result = await fetchChannelMembers(membersModal.id);
            setMembers(result.data || []);
            // Also refresh channels to update member count
            loadChannels();
          }}
          onMemberRemoved={async () => {
            // Refresh members list
            const result = await fetchChannelMembers(membersModal.id);
            setMembers(result.data || []);
            // Also refresh channels to update member count
            loadChannels();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {channelToDelete && (
        <DeleteConfirmModal
          channel={channelToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setChannelToDelete(null)}
          isDeleting={deletingId === channelToDelete.id}
        />
      )}
    </div>
  );
}
